import type { Dashboard, NotificationList, MarkRead, UsageSummary } from '@schwarz/contract';

import { apiSlice } from './api-slice.js';

/** How often the notification bell re-reads. Q8: 15 seconds, no SSE in v1. */
export const NOTIFICATION_POLL_MS = 15_000;

/**
 * How often the dashboard re-reads.
 *
 * Faster while a call is in flight or the queue holds something, because that is
 * when a figure is about to move; slower otherwise, since ten aggregate queries
 * every fifteen seconds to watch nothing happen is a cost with no reader.
 */
export const DASHBOARD_BUSY_POLL_MS = 5000;
export const DASHBOARD_IDLE_POLL_MS = 45_000;

/**
 * Dashboard and notifications (S11, S12).
 *
 * The dashboard refetches on mount rather than polling: it is a page somebody
 * opens to look at, and every figure on it moves on the timescale of a call. The
 * bell polls, because its whole purpose is to tell you about something you were
 * not looking for.
 */
export const dashboardApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getDashboard: build.query<Dashboard, void>({
      // Every tag it draws from, so an action taken elsewhere in the console
      // refreshes the numbers immediately rather than at the next poll.
      providesTags: ['Contact', 'Interaction', 'Queue', 'Agent'],
      query: () => ({ url: '/dashboard' }),
    }),

    getUsage: build.query<UsageSummary, void>({
      query: () => ({ url: '/dashboard/usage' }),
      providesTags: ['Setting'],
    }),

    getNotifications: build.query<NotificationList, void>({
      query: () => ({ url: '/notifications' }),
      providesTags: ['Notification'],
    }),

    markNotificationsRead: build.mutation<{ read: number }, MarkRead>({
      query: (body) => ({ url: '/notifications/read', method: 'POST', body }),
      invalidatesTags: ['Notification'],
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetUsageQuery,
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
} = dashboardApi;
